import { Injectable } from '@angular/core';
import { EnumObjectExtended } from '@card-software/library';
import { forkJoin, Observable } from 'rxjs';
import { OrderEnumsProxyService } from '../proxies/order-enums.proxy.service';
import { ProductionEnumsProxyService } from '../proxies/production-enums.proxy.service';
import { LocaleHelperService } from './locale-helper.service';
import _ from 'lodash';
import { ItemEnumProxyService } from '@app/proxies/item-enums.proxy.service';

@Injectable({
  providedIn: 'root',
})
export class EnumService {
  private readonly itemEnumLocalStorageKey = 'itemEnums';
  private readonly orderEnumLocalStorageKey = 'orderEnums';
  private readonly inventoryEnumLocalStorageKey = 'inventoryEnums';
  private readonly productionEnumLocalStorageKey = 'productionEnums';
  constructor(
    private readonly orderEnumsProxyService: OrderEnumsProxyService,
    private readonly productionEnumsProxyService: ProductionEnumsProxyService,
    private readonly itemEnumsProxyService: ItemEnumProxyService,
    private readonly localeHelperService: LocaleHelperService,
  ) {}

  public loadEnums(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      forkJoin([
        this.orderEnumsProxyService.getAll(),
        this.productionEnumsProxyService.getAll(),
        this.itemEnumsProxyService.getAll(),
      ]).subscribe({
        next: (response) => {
          localStorage.removeItem(this.orderEnumLocalStorageKey);
          localStorage.removeItem(this.productionEnumLocalStorageKey);
          localStorage.removeItem(this.itemEnumLocalStorageKey);
          localStorage.setItem(this.orderEnumLocalStorageKey, JSON.stringify(response[0]));
          localStorage.setItem(this.productionEnumLocalStorageKey, JSON.stringify(response[1]));
          localStorage.setItem(this.itemEnumLocalStorageKey, JSON.stringify(response[2]));

          observer.next(true);
          observer.complete();
        },
        error: (error) => {
          console.error('Error loading enums', error);
          observer.next(false);
          observer.complete();
        },
      });
    });
  }

  public getEnum(enumName: string, domain: 'orderEnums' | 'productionEnums' | 'itemEnums'): EnumObjectExtended {
    let enumObj = null;

    switch (domain) {
      case 'productionEnums':
        enumObj = JSON.parse(localStorage.getItem(this.productionEnumLocalStorageKey)!);
        break;
      case 'orderEnums':
        enumObj = JSON.parse(localStorage.getItem(this.orderEnumLocalStorageKey)!);
        break;
      case 'itemEnums':
        enumObj = JSON.parse(localStorage.getItem(this.itemEnumLocalStorageKey)!);
        break;
    }

    if (!enumObj) {
      this.loadEnums().subscribe({
        next: (response) => {
          if (response) {
            this.getEnum(enumName, domain);
          }
        },
        error: (error) => {
          console.error('Error loading enums', error);
        },
      });
      //return an empty object
      return {
        name: '',
        values: {},
        valuesEnglish: {},
      };
    }

    const obj = enumObj.find(
      (x: { name: string; values: Record<string, string | Record<string, string>>; valuesEnglish?: Record<string, string> }) =>
        x.name === enumName,
    );
    if (obj) {
      // Translate enum values
      const translatedValues: Record<string, string> = {};
      for (const key in obj.values) {
        translatedValues[key] = _.startCase(this.localeHelperService.getLocalizedValue(obj.values[key]));
      }

      const englishValues: Record<string, string> = {};
      if (obj.valuesEnglish) {
        for (const key in obj.valuesEnglish) {
          englishValues[key] = obj.valuesEnglish[key];
        }
      } else {
        for (const key in obj.values) {
          const value = obj.values[key];
          englishValues[key] = typeof value === 'object' && value.en ? value.en : value;
        }
      }

      return { name: obj.name, values: translatedValues, valuesEnglish: englishValues };
    } else {
      return { name: enumName, values: {}, valuesEnglish: {} };
    }
  }
}
